<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Contas a Receber</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.dataInicio" type="date" label="Vencimento inicial" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.dataFim" type="date" label="Vencimento final" solo hide-details clearable class="c-filtro" />

      <v-autocomplete v-model="filtros.cliente" :items="clienteOptions" label="Cliente" clearable solo hide-details class="c-filtro" />
      <v-autocomplete v-model="filtros.job" :items="jobOptions" label="JOB" clearable solo hide-details class="c-filtro" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table :headers="headers" :items="dadosFiltrados" :loading="loading" item-key="doc" rows-per-page-text="Linhas por página:" page-text="{0}-{1} de {2}" class="elevation-1">
        <template v-slot:item.numero="{ item }">{{ item.numero }}</template>
        <template v-slot:item.vencimento="{ item }">{{ formatarDataPtBr(item.vencimento) }}</template>
        <template v-slot:item.dataRecebimento="{ item }">{{ formatarDataPtBr(item.dataRecebimento) }}</template>
        <template v-slot:item.valorBruto="{ item }">{{ formatarMoedaPtBr(item.valorBruto) }}</template>
        <template v-slot:item.impostos="{ item }">{{ formatarMoedaPtBr(item.impostos) }}</template>
        <template v-slot:item.valorLiquido="{ item }">{{ formatarMoedaPtBr(item.valorLiquido) }}</template>
        <template v-slot:item.saldo="{ item }">{{ formatarMoedaPtBr(item.saldo) }}</template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioContasAReceberService from "@/views/Relatorios/services/relatorioContasAReceberService";
import { exportarExcel, exportarPdfTabela, formatarDataPtBr, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";

export default {
  name: "RelatorioContasAReceberView",
  data() {
    return {
      loading: false,
      dados: [],
      filtros: { dataInicio: "", dataFim: "", cliente: "", job: "" },
      headers: [
        { text: "Número da NF", value: "numero" },
        { text: "Cliente", value: "cliente" },
        { text: "Doc", value: "doc" },
        { text: "Valor Bruto", value: "valorBruto" },
        { text: "Impostos", value: "impostos" },
        { text: "Valor Líquido", value: "valorLiquido" },
        { text: "Vencimento", value: "vencimento" },
        { text: "JOB", value: "job" },
        { text: "Data Recebimento", value: "dataRecebimento" },
        { text: "Banco", value: "banco" },
        { text: "Saldo", value: "saldo" }
      ]
    };
  },
  computed: {
    clienteOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.cliente)).filter(Boolean))).sort();
    },
    jobOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.job)).filter(Boolean))).sort();
    },
    dadosFiltrados() {
      const inicio = this.filtros.dataInicio ? new Date(this.filtros.dataInicio) : null;
      const fim = this.filtros.dataFim ? new Date(this.filtros.dataFim) : null;
      if (fim) fim.setHours(23, 59, 59, 999);

      return this.dados.filter((item) => {
        const temNumeroNF = item.numero && item.numero !== "-";
        if (!temNumeroNF) return false;

        const dataLinha = item.vencimento ? new Date(item.vencimento) : null;
        const clienteOk = !this.filtros.cliente || normalizarTexto(item.cliente) === normalizarTexto(this.filtros.cliente);
        const jobOk = !this.filtros.job || normalizarTexto(item.job) === normalizarTexto(this.filtros.job);

        if (!dataLinha || Number.isNaN(dataLinha.getTime())) return clienteOk && jobOk;

        const dataInicioOk = !inicio || dataLinha >= inicio;
        const dataFimOk = !fim || dataLinha <= fim;

        return clienteOk && jobOk && dataInicioOk && dataFimOk;
      });
    }
  },
  watch: {
    'filtros.dataInicio'() { this.buscarDados(); },
    'filtros.dataFim'() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioContasAReceberService.listarContasAReceber(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      const linhas = this.dadosFiltrados.map((item) => ({
        "Número da NF": item.numero,
        "Cliente": item.cliente,
        "Doc": item.doc,
        "Valor Bruto": this.formatarMoedaPtBr(item.valorBruto),
        "Impostos": this.formatarMoedaPtBr(item.impostos),
        "Valor Líquido": this.formatarMoedaPtBr(item.valorLiquido),
        "Vencimento": this.formatarDataPtBr(item.vencimento),
        "JOB": item.job,
        "Data Recebimento": this.formatarDataPtBr(item.dataRecebimento),
        "Banco": item.banco,
        "Saldo": this.formatarMoedaPtBr(item.saldo)
      }));
      exportarExcel(linhas, "relatorio-contas-a-receber.xlsx");
    },
    async gerarPdf() {
      await exportarPdfTabela({
        headers: this.headers,
        rows: this.dadosFiltrados,
        nomeArquivo: "relatorio-contas-a-receber.pdf",
        tituloRelatorio: "Relatório de Contas a Receber",
        formatadoresPorCampo: {
          numero: (valor) => valor,
          vencimento: (valor) => this.formatarDataPtBr(valor),
          dataRecebimento: (valor) => this.formatarDataPtBr(valor),
          valorBruto: (valor) => this.formatarMoedaPtBr(valor),
          impostos: (valor) => this.formatarMoedaPtBr(valor),
          valorLiquido: (valor) => this.formatarMoedaPtBr(valor),
          saldo: (valor) => this.formatarMoedaPtBr(valor)
        }
      });
    }
  },
  async mounted() {
    await this.buscarDados();
  }
};
</script>

<style scoped>
.c-filtro { min-width: 220px; max-width: 280px; }
</style>
